import usePlacesAutocomplete, { GeocodeResult, getGeocode } from "use-places-autocomplete"
import useOnclickOutside from "react-cool-onclickoutside"
import React, { useEffect, useMemo, useState } from "react"
import { useApiIsLoaded } from "@vis.gl/react-google-maps"

interface UseAddressAutocompleteProps {
	defaultValue?: string
	defaultCoords?: { lat: number; lng: number }
	onChangeAddress?: (address: GeocodeResult) => void
}

export function useAddressAutocomplete({
	defaultValue,
	defaultCoords,
	onChangeAddress,
}: UseAddressAutocompleteProps) {
	const isLoaded = useApiIsLoaded()
	const { init, ready, value, suggestions, setValue, clearSuggestions } =
		usePlacesAutocomplete({
			requestOptions: {},
			initOnMount: false,
			defaultValue: defaultValue?.toString(),
			debounce: 300,
		})

	React.useEffect(() => {
		if (defaultCoords && ready) {
			findByLatLng(defaultCoords.lat, defaultCoords.lng)
		} else if (defaultValue && ready) {
			findByAddress(defaultValue)
		}
	}, [ready])

	useEffect(() => {
		if (isLoaded) {
			init()
		}
	}, [isLoaded])

	const ref = useOnclickOutside(() => {
		clearSuggestions()
	})

	const findByAddress = async (address: string) => {
		setValue(address, false)
		clearSuggestions()

		try {
			const results = await getGeocode({ address })
			const result = results[0]
			if (!result) {
				return
			}

			onChangeAddress?.(result)
			setSelectedPlace(result)
		} catch (error) {
			console.error("findByAddress error: ", error)
		}
	}

	const findByLatLng = async (lat: number, lng: number) => {
		try {
			const results = await getGeocode({ location: { lat, lng } })
			const result = results[0]
			if (!result) {
				return
			}

			onChangeAddress?.(result)
			setSelectedPlace(result)
			setValue(result.formatted_address, false)
		} catch (error) {
			console.error("findByLatLng error: ", error)
		}
	}

	const [selectedPlace, setSelectedPlace] = useState<GeocodeResult | null>(null)

	const marker = useMemo(() => {
		let location = selectedPlace?.geometry?.location
		if (!location) return

		return { lat: location.lat(), lng: location.lng() }
	}, [selectedPlace])

	return {
		ready,
		value,
		suggestions: suggestions.data,
		setSuggest: setValue,
		findByLatLng,
		findByAddress,
		ref,
		selectedPlace,
		marker,
	}
}
